import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { confirmDelete } from '../../utils/confirm';

function MasterValueModal({ tenantId, segment, valueRecord, onClose, onSaved }) {
  const isEdit = !!valueRecord;
  const [form, setForm] = useState({
    valueName: valueRecord?.ValueName || '',
    isActive: valueRecord ? !!valueRecord.IsActive : true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!segment?.SegmentCode) return toast.error('Please select a master segment first');
    if (!form.valueName.trim()) return toast.error('Value is required');

    setSaving(true);
    try {
      const payload = {
        tenantId,
        segmentCode: segment.SegmentCode,
        valueName: form.valueName.trim(),
        isActive: form.isActive,
      };

      if (isEdit) {
        await api.put(`/qp-settings/master-values/${valueRecord.MasterValueID}`, payload);
        toast.success('Value updated');
      } else {
        await api.post('/qp-settings/master-values', payload);
        toast.success('Value added');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save value');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit Master Value' : `Add Value - ${segment?.SegmentName || ''}`}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="required-label">Value</label>
            <input
              type="text"
              placeholder="e.g., CO1"
              value={form.valueName}
              onChange={(e) => setForm({ ...form, valueName: e.target.value })}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Is Active
            </label>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-save" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update Value' : 'Add Value'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MasterValuesTab({ tenantId }) {
  const [segments, setSegments] = useState([]);
  const [selectedSegmentCode, setSelectedSegmentCode] = useState('');
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editValue, setEditValue] = useState(null);

  const selectedSegment = segments.find((s) => s.SegmentCode === selectedSegmentCode) || null;

  const loadSegments = useCallback(async () => {
    try {
      const res = await api.get('/qp-settings/master-segments');
      if (res.data.success) {
        const data = res.data.data || [];
        setSegments(data);
        // Set default to first segment only on initial load (functional update prevents stale closure)
        setSelectedSegmentCode((prev) => prev || (data[0]?.SegmentCode ?? ''));
      }
    } catch {
      toast.error('Failed to load master segments');
    }
  }, []);

  const loadValues = useCallback(async () => {
    if (!tenantId || !selectedSegmentCode) {
      setValues([]);
      return;
    }

    try {
      setLoading(true);
      const res = await api.get(`/qp-settings/master-values/${tenantId}`, {
        params: { segmentCode: selectedSegmentCode },
      });
      if (res.data.success) {
        setValues(res.data.data || []);
      }
    } catch {
      toast.error('Failed to load master values');
    } finally {
      setLoading(false);
    }
  }, [tenantId, selectedSegmentCode]);

  useEffect(() => {
    loadSegments();
  }, [loadSegments]);

  useEffect(() => {
    loadValues();
  }, [loadValues]);

  const handleDelete = async (masterValueId) => {
    if (!(await confirmDelete('Master Value'))) return;

    try {
      await api.delete(`/qp-settings/master-values/${masterValueId}`);
      toast.success('Value deleted');
      loadValues();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete value');
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <>
      <div className="page-card-header" style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}>
        <h3>Master Values {selectedSegment && <span style={{ fontWeight: 400, fontSize: 14, color: '#616161' }}>({selectedSegment.SegmentName})</span>}</h3>
        <button
          className="btn btn-add"
          onClick={() => { setEditValue(null); setShowModal(true); }}
          disabled={!selectedSegmentCode}
        >
          + Add Value
        </button>
      </div>

      <div className="master-values-toolbar">
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="required-label">Master Segment</label>
          <select
            className="filter-select"
            value={selectedSegmentCode}
            onChange={(e) => setSelectedSegmentCode(e.target.value)}
            style={{ minWidth: 280 }}
          >
            {segments.length === 0 && <option value="">No segments available</option>}
            {segments.map((segment) => (
              <option key={segment.SegmentCode} value={segment.SegmentCode}>
                {segment.SegmentName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!selectedSegmentCode ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          Select a master segment to configure values.
        </div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : values.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No values configured for <strong>{selectedSegment?.SegmentName}</strong>. Click "Add Value" to create one.
        </div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Value</th>
              <th>Status</th>
              <th>Updated At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {values.map((value) => (
              <tr key={value.MasterValueID}>
                <td><strong>{value.ValueName}</strong></td>
                <td>
                  <span className={`badge ${value.IsActive ? 'badge-active' : 'badge-inactive'}`}>
                    {value.IsActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ color: '#616161', fontSize: 13 }}>{formatDate(value.UpdatedAt)}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="action-btn action-edit"
                      title="Edit"
                      onClick={() => { setEditValue(value); setShowModal(true); }}
                    >
                      ✏
                    </button>
                    <button
                      className="action-btn action-delete"
                      title="Delete"
                      onClick={() => handleDelete(value.MasterValueID)}
                    >
                      🗑
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && selectedSegment && (
        <MasterValueModal
          tenantId={tenantId}
          segment={selectedSegment}
          valueRecord={editValue}
          onClose={() => { setShowModal(false); setEditValue(null); }}
          onSaved={() => { setShowModal(false); setEditValue(null); loadValues(); }}
        />
      )}
    </>
  );
}

export default MasterValuesTab;
